<template>
    <div>
      <div class="date-range">
        <div class="date-range__presets" v-if="!noPresets">
          <v-list :dark="dark">
            <v-subheader>{{ labels.preset }}</v-subheader>
            <v-list-tile
              :key="index"
              @click="onPresetSelect(index)"
              v-for="(preset, index) in presets"
              v-model="isPresetActive[index]"
            >
              <v-list-tile-content>{{ preset.label }}</v-list-tile-content>
            </v-list-tile>
          </v-list>
        </div>
        <div class="date-range__pickers">
          <div class="date-range__picker date-range__pickers--start">
            <v-text-field
              :label="labels.start"
              :prepend-icon="prependIcon"
              class="date-range__pickers-input"
              name="startDate"
              v-model="formattedStartDate"
              @input="dateTypedStart"
              v-bind="attrs"
              v-on="on"
              :rules="[rules.formatRequired]"
            ></v-text-field>
            <v-date-picker
              :dark="dark"
              :event-color="highlightRange ? dateRange.colors : eventColor"
              :events="highlightRange ? dateRange.dates : events"
              :first-day-of-week="firstDayOfWeek"
              :locale="locale"
              :max="endDate"
              :min="options.minDate"
              :next-icon="nextIcon"
              :prev-icon="prevIcon"
              no-title
              v-model="startDate"
              @change="onDateRangeChange"
            ></v-date-picker>
          </div>
          <div class="date-range__picker date-range__picker--end">
            <v-text-field
              :label="labels.end"
              class="date-range__pickers-input"
              name="endDate"
              v-model="formattedEndDate"
              @input="dateTypedEnd"
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
            <v-date-picker
              :dark="dark"
              :event-color="highlightRange ? dateRange.colors : eventColor"
              :events="highlightRange ? dateRange.dates : events"
              :first-day-of-week="firstDayOfWeek"
              :locale="locale"
              :max="maxDate"
              :min="startDate"
              :next-icon="nextIcon"
              :prev-icon="prevIcon"
              no-title
              v-model="endDate"
              @change="onDateRangeChange"
            ></v-date-picker>
          </div>
        </div>
      </div>
      <v-layout class="mt-2" row wrap>
        <v-spacer></v-spacer>
        <v-btn @click="cancelPicker()" color="primary" flat>Cancel</v-btn>
        <v-btn @click="filterDateRange()" color="primary" flat>OK</v-btn>
      </v-layout>
    </div>
  </template>
  
  <script>
  import { format, parse } from "date-fns";
  export default {
    name: "v-date-range",
    inheritAttrs: false,
    props: {
      options: {
        type: Object,
        required: true,
      },
      noPresets: {
        type: Boolean,
        default: false,
      },
      dark: {
        type: Boolean,
        default: false,
      },
      prependIcon: {
        type: String,
        default: "event",
      },
      nextIcon: {
        type: String,
        default: "chevron_right",
      },
      prevIcon: {
        type: String,
        default: "chevron_left",
      },
      labels: {
        type: Object,
        default() {
          return {
            start: "Start Date",
            end: "End Date",
            preset: "Presets",
          };
        },
      },
      events: {
        type: [Array, Object, Function],
        default: () => null,
      },
      eventColor: {
        type: [String, Function, Object],
        default: "warning",
      },
      highlightRange: {
        type: Boolean,
        default: true,
      },
      highlightColors: {
        type: String,
        default: "",
      },
      locale: {
        type: String,
        default: "en-us",
      },
      firstDayOfWeek: {
        type: Number,
        default: 0,
      },
    },
    data() {
      return {
        startDate: this.options.startDate,
        endDate: this.options.endDate,
        format: this.options.format,
        presets: this.options.presets,
        dateRange: {
          dates: [],
          colors: {},
        },
        rules: {
          formatRequired: (value) => {
            // eslint-disable-next-line no-useless-escape
            const pattern = /^([0-2][0-9]|(3)[0-1])(\/)(((0)[0-9])|((1)[0-2]))(\/)\d{4}$/; 
            if (!value || !value.length) return true;
            return pattern.test(value) || "Invalid format date";
          }
        }
      };
    },
    computed: {
      attrs() {
        return this.$attrs || {};
      },
      on() {
        return this.$listeners || {};
      },
      formattedStartDate() {
        return this.startDate ? this.formatDateForInput(this.startDate) : "";
      },
      formattedEndDate() {
        return this.endDate ? this.formatDateForInput(this.endDate) : "";
      },
      highlightColorClasses() {
        if (this.highlightColors) {
          return this.highlightColors;
        }
        return this.dark ? "blue-grey darken-1" : "blue lighten-5";
      },
      isPresetActive() {
        return this.presets.map(
          (preset) =>
            preset.range[0] === this.startDate && preset.range[1] === this.endDate
        );
      },
      today() {
        return format(new Date(), "YYYY-MM-DD");
      },
      maxDate() {
        return this.options.maxDate || this.today;
      },
    },
    watch: {
      startDate() {
        this.onDateRangeChange();
      },
      endDate() {
        this.onDateRangeChange();
      },
    },
    mounted() {
      if (this.highlightRange) this.setInRangeData();
    },
    methods: {
      formatDateForInput(date) {
        if (!date) return null;
        const [year, month, day] = date.split("-");
        return `${day}/${month}/${year}`;
      },
      onPresetSelect(presetIndex) {
        this.startDate = this.presets[presetIndex].range[0];
        this.endDate = this.presets[presetIndex].range[1];
      },
      onDateRangeChange() {
        if (this.highlightRange) this.setInRangeData();
        this.$emit("input", [this.startDate, this.endDate]);
      },
      setInRangeData() {
        const inRangeData = {
          dates: [],
          colors: {},
        };
        if (this.highlightRange) {
          const startDate = parse(this.startDate);
          const endDate = parse(this.endDate);
          const diffDays = (endDate - startDate) / (1000 * 3600 * 24);
          for (let i = 0; i <= diffDays; i += 1) {
            const date = this.addDays(startDate.toDateString(), i);
            inRangeData.dates.push(date);
            inRangeData.colors[
              date
            ] = `date-range__date-in-range ${this.highlightColorClasses}`;
            if (i === 0) inRangeData.colors[date] += " date-range__range-start";
            if (i === diffDays)
              inRangeData.colors[date] += " date-range__range-end";
          }
        }
        this.dateRange = inRangeData;
      },
      addDays(date, days) {
        const result = parse(date);
        result.setDate(result.getDate() + days);
        return format(result, "YYYY-MM-DD");
      },
      cancelPicker() {
        this.startDate = this.options.startDate;
        this.endDate = this.options.endDate;
  
        this.$emit("cancelPicker");
      },
      filterDateRange() {
        this.$emit("filterDateRange");
      },
      dateTypedStart(date) {
        if (!date || date.length !== 10) return;
  
        this.startDate = this.formatDateForPicker(date);
      },
      dateTypedEnd(date) {
        if (!date || date.length !== 10) return;
  
        this.endDate = this.formatDateForPicker(date);
      },
      formatDateForPicker(date) {
        if (!date) return null
        const [day, month, year] = date.split(/[\s,/.]+/);
        return `${year}-${month}-${day}`;
      },
    },
  };
  </script>
  
  <style scoped>
  .date-range {
    display: flex;
  }
  .date-range__presets {
    margin-right: 1rem;
  }
  .date-range__pickers {
    display: flex;
    overflow-x: auto;
    padding-bottom: 15px;
  }
  .date-range__picker {
    padding: 0 1rem;
  }
  .date-range >>> .date-picker-table table,
  .date-range >>> .v-date-picker-table table {
    border-collapse: collapse;
  }
  .date-range >>> .date-picker-table__event.date-range__date-in-range,
  .date-range >>> .v-date-picker-table__event.date-range__date-in-range {
    z-index: 0;
    /* override existing settings */
    width: 100%;
    height: 100%;
    left: 0;
    top: 0;
    bottom: 0;
    border-radius: 0;
  }
  .date-range
    >>> .date-picker-table__event.date-range__date-in-range.date-range__range-start,
  .date-range
    >>> .v-date-picker-table__event.date-range__date-in-range.date-range__range-start {
    border-top-left-radius: 50%;
    border-bottom-left-radius: 50%;
    /* Cover only date button */
    left: 7px;
    width: 31px;
  }
  .date-range
    >>> .date-picker-table__event.date-range__date-in-range.date-range__range-end,
  .date-range
    >>> .v-date-picker-table__event.date-range__date-in-range.date-range__range-end {
    border-top-right-radius: 50%;
    border-bottom-right-radius: 50%;
  }
  .date-range >>> .date-picker-table .btn,
  .date-range >>> .v-date-picker-table .v-btn {
    /* fixed zIndex is needed because .date-picker-table__event div is created after the .btn button */
    z-index: 1;
  }
  @media screen and (max-width: 768px) {
    .date-range {
      flex-direction: column;
    }
    .date-range__presets {
      margin-right: 0;
    }
    .date-range__presets .v-list {
      display: flex;
      justify-content: space-between;
    }
    .date-range__presets .v-subheader {
      display: none;
    }
  }
  @media screen and (max-width: 575px) {
    .date-range__presets .v-list {
      flex-wrap: wrap;
    }
    .date-range__presets .v-list .v-list__tile__content {
      justify-content: center;
    }
    .date-range__pickers {
      flex-direction: column;
    }
    .date-range__pickers > div {
      padding: 5px;
    }
    .date-range__pickers .v-picker >>> div:not(.date-range__date-in-range) {
      width: 100% !important;
    }
  }
  </style>
  